import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import logoUrl from "@/assets/sableboxx-logo.png"
import logoLightUrl from "@/assets/sableboxx-logo-light.png"
import { SITE } from "./site"
import { initials } from "./format"
import { useCollection } from "./content"

/*
  Stamped in at build time by vite.config.ts. Calling new Date() during render
  would differ between the prerendered HTML and the browser that hydrates it
  across a new year, which React reports as a hydration mismatch — and every
  visitor on 1 January would be the one to find it.
*/
declare const __BUILD_YEAR__: string
const BUILD_YEAR = __BUILD_YEAR__

/** useLayoutEffect is a no-op on the server; React warns if it is called there. */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect


/* Every image slot lives in images.ts, with instructions for swapping in
   your own. Re-exported so pages keep importing everything from here. */
export { IMG } from "./images"

/* ----------------------------------------------------------------------------
   Scroll reveal
---------------------------------------------------------------------------- */
/**
 * Scroll-reveal wrapper.
 *
 * The markup for every route is prerendered, so the browser paints real content
 * before any JavaScript runs. If `.reveal` defaulted to opacity:0 that first
 * paint would be blank — delaying LCP until hydration, and leaving the page
 * permanently invisible if the bundle fails to load.
 *
 * So content is visible by default and JS *opts an element into* the animation
 * ("arms" it) only when it starts below the fold. Anything already on screen is
 * simply left alone: it is visible in the static HTML and stays that way.
 */
export function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
}: {
  children: React.ReactNode
  delay?: number
  className?: string
  as?: React.ElementType
}) {
  const ref = useRef<HTMLElement | null>(null)
  const [armed, setArmed] = useState(false)
  const [shown, setShown] = useState(false)

  useIsomorphicLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    if (prefersReducedMotion) return

    // Already on screen at hydration: leave the painted content untouched.
    if (el.getBoundingClientRect().top < window.innerHeight) return

    setArmed(true)

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true)
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <Tag
      ref={ref as never}
      className={`reveal ${armed ? "is-armed" : ""} ${shown ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  )
}

/* ----------------------------------------------------------------------------
   Icons: thin line set, 24×24
---------------------------------------------------------------------------- */
type IconName =
  | "arrow"
  | "plus"
  | "identity"
  | "website"
  | "product"
  | "growth"
  | "credible"
  | "clarity"
  | "convert"
  | "strategy"
  | "design"
  | "tech"
  | "future"
  | "startup"
  | "rising"
  | "building"
  | "institution"
  | "foundation"
  | "scale"
  | "support"
  | "mail"
  | "phone"
  | "pin"
  | "check"
  | "quote"
  | "menu"
  | "close"

const PATHS: Record<IconName, React.ReactNode> = {
  arrow: <path d="M5 12h13M13 6l6 6-6 6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  identity: (
    <>
      <path d="M12 3l2.5 5.5L20 11l-4 4 1 6-5-3-5 3 1-6-4-4 5.5-2.5L12 3z" />
    </>
  ),
  website: (
    <>
      <rect x="3" y="4" width="18" height="15" rx="2" />
      <path d="M3 9h18M7 6.5h.01M9.5 6.5h.01" />
    </>
  ),
  product: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  growth: (
    <>
      <path d="M4 19h16" />
      <path d="M7 16v-4M12 16V8M17 16v-7" />
      <path d="M14 6l3-1 1 3" />
    </>
  ),
  credible: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  clarity: (
    <>
      <path d="M20 4H4a1 1 0 00-1 1v11a1 1 0 001 1h3v4l5-4h8a1 1 0 001-1V5a1 1 0 00-1-1z" />
    </>
  ),
  convert: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </>
  ),
  strategy: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" />
    </>
  ),
  design: (
    <>
      <path d="M16 3l5 5-11 11-5 1 1-5L16 3z" />
      <path d="M14 5l5 5" />
    </>
  ),
  tech: (
    <>
      <rect x="7" y="7" width="10" height="10" rx="1.5" />
      <path d="M10 2v3M14 2v3M10 19v3M14 19v3M2 10h3M2 14h3M19 10h3M19 14h3" />
    </>
  ),
  future: (
    <>
      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    </>
  ),
  startup: (
    <>
      <path d="M12 3c3 1.5 5 5 5 9l-2 4H9l-2-4c0-4 2-7.5 5-9z" />
      <circle cx="12" cy="10" r="1.6" />
      <path d="M9 18l-2 3M15 18l2 3" />
    </>
  ),
  rising: (
    <>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M15 7h6v6" />
    </>
  ),
  building: (
    <>
      <path d="M4 21V6l7-3v18M11 21h9V10l-9-3" />
      <path d="M14.5 11h.01M14.5 14h.01M14.5 17h.01M7.5 8h.01M7.5 11h.01M7.5 14h.01" />
    </>
  ),
  institution: (
    <>
      <path d="M3 10l9-6 9 6M5 10v9M9 10v9M15 10v9M19 10v9M3 21h18" />
    </>
  ),
  foundation: (
    <>
      <path d="M12 3l9 5-9 5-9-5 9-5z" />
      <path d="M3 12l9 5 9-5" />
    </>
  ),
  scale: (
    <>
      <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
      <path d="M12 8v8M8 10v4M16 10v4" />
    </>
  ),
  support: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M14.5 9.5l3-3M6.5 17.5l3-3M14.5 14.5l3 3M6.5 6.5l3 3" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M4 7l8 6 8-6" />
    </>
  ),
  phone: (
    <path d="M6 3h4l2 5-2.5 1.5a11 11 0 005 5L16 12l5 2v4a2 2 0 01-2 2A16 16 0 014 5a2 2 0 012-2z" />
  ),
  pin: (
    <>
      <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  check: <path d="M4 12l5 5L20 6" />,
  quote: (
    <path d="M9 7H5a1 1 0 00-1 1v4a1 1 0 001 1h3v1a2 2 0 01-2 2H5m14-9h-4a1 1 0 00-1 1v4a1 1 0 001 1h3v1a2 2 0 01-2 2h-1" />
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
}

export function Icon({
  name,
  className = "h-5 w-5",
}: {
  name: IconName
  className?: string
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  )
}

export const Arrow = ({ className = "" }: { className?: string }) => (
  <Icon name="arrow" className={className} />
)

/* ----------------------------------------------------------------------------
   Primitives
---------------------------------------------------------------------------- */
export function EyebrowLabel({
  children,
  light = false,
}: {
  children: React.ReactNode
  light?: boolean
}) {
  return (
    <span
      className={`inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.24em] ${
        light ? "text-accent-bright" : "text-accent"
      }`}
    >
      <span className={`h-px w-6 ${light ? "bg-accent-bright/60" : "bg-accent/60"}`} />
      {children}
    </span>
  )
}

export function ButtonLight({
  children,
  to,
  href,
}: {
  children: React.ReactNode
  to?: string
  /** External URL. Opens in a new tab, with the arrow pointing up and out. */
  href?: string
}) {
  const cls =
    "group inline-flex items-center gap-2.5 rounded-full bg-white px-5 py-3 text-[13px] font-medium text-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.16)]"
  const inner = (
    <>
      {children}
      <Arrow
        className={`h-4 w-4 transition-transform duration-300 ${
          href
            ? "-rotate-45 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            : "group-hover:translate-x-1"
        }`}
      />
    </>
  )
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
        {inner}
      </a>
    )
  }
  return to ? (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  ) : (
    <button className={cls}>{inner}</button>
  )
}

export function ButtonDark({
  children,
  to,
}: {
  children: React.ReactNode
  to?: string
}) {
  const cls =
    "group inline-flex items-center gap-2.5 rounded-full bg-ink px-5 py-3 text-[13px] font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-black/80"
  const inner = (
    <>
      {children}
      <Arrow className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  )
  return to ? (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  ) : (
    <button className={cls}>{inner}</button>
  )
}

export function TextLink({
  children,
  to = "#",
  light = false,
}: {
  children: React.ReactNode
  to?: string
  light?: boolean
}) {
  return (
    <Link
      to={to}
      className={`group inline-flex items-center gap-1.5 py-1.5 text-[13px] font-medium ${
        light ? "text-white/70 hover:text-white" : "text-ink"
      }`}
    >
      {children}
      <Arrow className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  )
}

/**
 * True once an <img> has failed to load. Also catches a failure that happened
 * before hydration, when the prerendered markup started loading the image
 * before React attached its onError handler.
 */
function useImageFailed(src: string) {
  const ref = useRef<HTMLImageElement>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    setFailed(false)
    const el = ref.current
    if (el && el.complete && el.naturalWidth === 0) setFailed(true)
  }, [src])
  return { ref, failed, onError: () => setFailed(true) }
}

/**
 * Stand-in for a missing image.
 *
 * Deliberately quiet. This used to be a dark panel carrying a blue gradient, a
 * grid overlay and the client's initials set at 11rem — which announced "image
 * missing" in the loudest way available and dominated every card it appeared
 * in. A placeholder is not a design opportunity: it should read as a plain
 * surface somebody has not put a photo on yet, and let the words beside it do
 * the work.
 */
function ImageFallback({
  className,
  alt,
  label,
  monogram,
}: {
  className: string
  alt: string
  label?: string
  monogram?: string
}) {
  return (
    <div
      className={`relative grid place-items-center overflow-hidden bg-neutral-100 ${className}`}
      role="img"
      aria-label={alt}
    >
      {monogram && (
        <span className="grid h-12 w-12 select-none place-items-center rounded-xl bg-ink text-[15px] font-bold tracking-[-0.02em] text-white">
          {monogram}
        </span>
      )}
      {label && (
        <span className="absolute bottom-5 left-5 rounded-full bg-ink px-3.5 py-1.5 text-[11.5px] font-semibold uppercase tracking-[0.14em] text-white">
          {label}
        </span>
      )}
    </div>
  )
}

/**
 * Content image.
 *
 * `priority` marks an image that is visible on first paint, typically the
 * largest-contentful-paint element. Those must NOT be lazy-loaded, because
 * deferring the LCP element is a direct Core Web Vitals penalty. Everything
 * else stays lazy and async-decoded.
 *
 * Intrinsic width/height are always emitted so the browser can reserve the
 * box before the bytes arrive; the aspect ratio comes from the wrapper's
 * utility classes, so the numbers only need to express the correct ratio.
 *
 * `natural` drops the fixed box: the image shows whole, at its own aspect
 * ratio, for work where cropping would cut off the thing being shown. The
 * width/height attributes then only reserve a provisional box until it loads.
 *
 * With no `src`, or one that fails to load, a branded panel stands in (with
 * `label` on it if given). Rendering <img src=""> would make the browser
 * re-request the current page as the image, and a broken-image icon or flat
 * grey box reads as a broken site.
 */
/*
  Uploads are stored on Cloudinary at whatever size and format they arrived in,
  often multi-megabyte PNGs. Asking Cloudinary for a modern format, automatic
  quality and a capped width cuts that by around 90%, and the srcset lets the
  browser pick the smallest width that fills the slot. A URL that already
  carries a transformation is left alone.
*/
const CLOUDINARY_WIDTHS = [640, 960, 1280, 1920, 2560]

function cloudinary(src: string, w: number) {
  const m = src.match(/^(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(?!(?:[a-z]{1,3}_[^/]+\/))(.+)$/)
  return m ? `${m[1]}f_auto,q_auto,c_limit,w_${w}/${m[2]}` : null
}

export function Img({
  src,
  alt,
  className = "",
  priority = false,
  width = 1600,
  height = 1000,
  label,
  monogram,
  natural = false,
  sizes = "(min-width: 1280px) 1200px, 100vw",
}: {
  src: string
  alt: string
  className?: string
  priority?: boolean
  /** Show the whole image at its own aspect ratio instead of cropping it. */
  natural?: boolean
  width?: number
  height?: number
  /** Text shown on the branded panel that stands in when there is no image. */
  label?: string
  /** Large faded initials for that panel, when a label would repeat nearby text. */
  monogram?: string
  /** How wide the image renders, so the browser picks the right file. */
  sizes?: string
}) {
  const { ref, failed, onError } = useImageFailed(src)
  const optimised = cloudinary(src ?? "", 1600)

  if (!src || failed) {
    return (
      <ImageFallback
        className={natural ? `aspect-[16/10] ${className}` : className}
        alt={alt}
        label={label}
        monogram={monogram}
      />
    )
  }

  return (
    <div className={`overflow-hidden bg-neutral-200 ${className}`}>
      <img
        ref={ref}
        src={optimised ?? src}
        srcSet={
          optimised
            ? CLOUDINARY_WIDTHS.map((w) => `${cloudinary(src, w)} ${w}w`).join(", ")
            : undefined
        }
        sizes={optimised ? sizes : undefined}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        decoding={priority ? "sync" : "async"}
        onError={onError}
        className={`w-full transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
          natural ? "block h-auto" : "h-full object-cover"
        }`}
      />
    </div>
  )
}

/**
 * Decorative background photo for a dark hero. Always above the fold, so it
 * loads eagerly. If it fails it simply disappears and the ink background and
 * gradients carry the section on their own.
 */
export function BgImage({
  src,
  className = "",
  style,
}: {
  src: string
  className?: string
  style?: React.CSSProperties
}) {
  const { ref, failed, onError } = useImageFailed(src)
  if (!src || failed) return null
  return (
    <img
      ref={ref}
      src={src}
      alt=""
      fetchPriority="high"
      decoding="sync"
      onError={onError}
      className={`h-full w-full object-cover ${className}`}
      style={style}
    />
  )
}

/**
 * A full-width photograph that breaks up a long page, carrying one large
 * statement. Two gradients keep the words legible on any photo: one rising
 * from the bottom, one from the left edge where the text sits. Sits on the
 * background of the section above it (`tone`), so the band never floats.
 */
export function ImageBand({
  src,
  alt,
  eyebrow,
  caption,
  tone = "paper",
}: {
  src: string
  alt: string
  eyebrow?: string
  caption?: string
  tone?: Tone
}) {
  return (
    <section className={`${TONES[tone]} pb-24 lg:pb-32`}>
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-ink">
            <Img
              src={src}
              alt={alt}
              width={2400}
              height={1030}
              className="aspect-[4/5] sm:aspect-[16/10] lg:aspect-[21/9] [&_img]:animate-[drift_24s_ease-in-out_infinite_alternate]"
            />
            {caption && (
              <>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/10" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7 sm:p-10 lg:p-16">
                  {eyebrow && <EyebrowLabel light>{eyebrow}</EyebrowLabel>}
                  <p className="mt-5 max-w-3xl text-balance text-[clamp(2rem,4.4vw,3.75rem)] font-black leading-[1.03] tracking-[-0.035em] text-white">
                    {caption}
                  </p>
                </div>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

/*
  Section tones. Light sections alternate paper and mist; ink is punctuation,
  used sparingly. Passing `tone` also applies the standard vertical rhythm, so
  every full section on the site has the same breathing room. Leave it off
  (and pass className) only for strips that are deliberately tighter.
*/
export type Tone = "paper" | "mist" | "ink"

const TONES: Record<Tone, string> = {
  paper: "bg-paper text-ink",
  mist: "bg-mist text-ink",
  ink: "bg-ink text-white",
}

export const Section = ({
  children,
  className = "",
  id,
  tone,
}: {
  children: React.ReactNode
  className?: string
  id?: string
  tone?: Tone
}) => (
  <section
    id={id}
    className={`${tone ? `${TONES[tone]} py-24 lg:py-32` : ""} ${className}`}
  >
    <div className="mx-auto max-w-[1280px] px-6 lg:px-10">{children}</div>
  </section>
)

/**
 * The one section header used across the site.
 *
 * Split (default): eyebrow and heading on the left, a supporting paragraph
 * and optional action on the right, aligned to the heading's baseline. A
 * heading never sits alone with an empty half-page beside it, which is what
 * made the old sections look unfinished.
 *
 * Stacked: everything in one column, for narrow layouts such as a header
 * inside half of a two-column section.
 *
 * It owns the gap to the content below, so that gap is identical everywhere.
 */
export function SectionHeader({
  eyebrow,
  title,
  intro,
  action,
  light = false,
  layout = "split",
  as: Heading = "h2",
  className = "",
}: {
  eyebrow: string
  title: React.ReactNode
  intro?: React.ReactNode
  action?: React.ReactNode
  light?: boolean
  layout?: "split" | "stacked" | "centered"
  as?: "h1" | "h2"
  className?: string
}) {
  const heading = (
    <>
      <Reveal>
        <EyebrowLabel light={light}>{eyebrow}</EyebrowLabel>
      </Reveal>
      <Reveal delay={80}>
        <Heading className="mt-5 text-[clamp(2rem,3.6vw,3rem)] font-black leading-[1.06] tracking-[-0.03em]">
          {title}
        </Heading>
      </Reveal>
    </>
  )

  const aside = (intro || action) && (
    <Reveal delay={140}>
      {intro && (
        <p
          className={`text-[16px] leading-[1.7] ${
            light ? "text-neutral-400" : "text-neutral-600"
          }`}
        >
          {intro}
        </p>
      )}
      {action && <div className={intro ? "mt-6" : ""}>{action}</div>}
    </Reveal>
  )

  if (layout === "stacked") {
    return (
      <div className={`mb-12 max-w-2xl lg:mb-14 ${className}`}>
        {heading}
        {aside && <div className="mt-6">{aside}</div>}
      </div>
    )
  }

  /*
    Centred: for a section whose content below is symmetrical — a grid of cards
    rather than a list that starts at the left edge.

    Half the section width on a desktop, which is what keeps a centred heading
    from running the full 1200px and reading as a banner. It widens on the way
    down, because half of a phone is not a column, it is a gutter.
  */
  if (layout === "centered") {
    return (
      <div
        className={`mb-14 flex flex-col lg:mb-16 lg:items-center lg:text-center ${className}`}
      >
        <div className="w-full md:w-4/5 lg:w-5/10">
          {heading}
          {aside && <div className="mt-6">{aside}</div>}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`mb-14 grid grid-cols-1 gap-6 lg:mb-16 lg:grid-cols-12 lg:items-end lg:gap-16 ${className}`}
    >
      <div className="lg:col-span-7">{heading}</div>
      {aside && <div className="lg:col-span-5 lg:pb-1.5">{aside}</div>}
    </div>
  )
}

/* Reusable page header for interior pages */
/*
  Pulses travelling along faint circuit lines, behind dark sections. Faded out
  on the left, where the headline sits, so it never competes with the text.
  Place it after any background image: same stacking layer, painted later.
*/
const SIGNAL_PATHS = [
  "M1440 90 H1120 a20 20 0 0 0 -20 20 V190 a20 20 0 0 1 -20 20 H760",
  "M1440 250 H1260 a20 20 0 0 0 -20 20 V340 a20 20 0 0 1 -20 20 H880",
  "M1440 420 H1180 a20 20 0 0 1 -20 -20 V330 a20 20 0 0 0 -20 -20 H980",
  "M1300 0 V120 a20 20 0 0 0 20 20 H1440",
  "M1040 600 V500 a20 20 0 0 1 20 -20 H1440",
]

export function SignalField() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 1440 600"
      preserveAspectRatio="xMaxYMid slice"
      className="pointer-events-none absolute inset-0 -z-10 h-full w-full [mask-image:linear-gradient(to_right,transparent_58%,black_92%)]"
    >
      {SIGNAL_PATHS.map((d, i) => (
        <g key={d} fill="none" strokeLinecap="round">
          <path d={d} stroke="rgba(255,255,255,0.09)" strokeWidth="1.5" />
          <path
            d={d}
            pathLength={100}
            strokeDasharray="7 93"
            stroke="#38b6ff"
            strokeWidth="2"
            className="animate-[signal_8s_linear_infinite]"
            style={{ animationDelay: `${i * -1.7}s` }}
          />
        </g>
      ))}
    </svg>
  )
}

export function PageHero({
  eyebrow,
  title,
  intro,
  image,
  centered = false,
}: {
  eyebrow: string
  title: React.ReactNode
  intro: string
  image?: string
  /** Full viewport height, everything centred — on desktop. Below lg it falls
      back to the default hero's height and left alignment, since a centred
      full-screen block on a phone is mostly empty scroll. For a page that
      opens on a statement rather than on content to scroll straight into. */
  centered?: boolean
}) {
  if (centered) {
    return (
      <section className="relative isolate flex items-center justify-center overflow-hidden bg-ink text-white lg:min-h-screen">
        {image && (
          <div className="absolute inset-0 -z-10">
            <BgImage src={image} className="opacity-40" />
            {/*
              Even scrims, not the left-weighted pair the default hero uses.
              That gradient exists to darken the side the text sits on; with
              the text in the middle it would shade the wrong half and leave
              the heading sitting on the bright part of the photograph.
            */}
            <div className="absolute inset-0 bg-ink/55" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-ink/75" />
          </div>
        )}
        <SignalField />
        <div className="mx-auto w-full max-w-[1280px] px-6 pb-24 pt-40 lg:px-10 lg:py-32 lg:text-center">
          <Reveal>
            <EyebrowLabel light>{eyebrow}</EyebrowLabel>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-7 max-w-4xl text-[clamp(2.7rem,5.2vw,4.4rem)] lg:mx-auto font-black leading-[1.02] tracking-[-0.035em]">
              {title}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-7 max-w-2xl text-[17px] leading-[1.75] text-neutral-300 lg:mx-auto">
              {intro}
            </p>
          </Reveal>
        </div>
      </section>
    )
  }

  return (
    <section className="relative isolate overflow-hidden bg-ink text-white">
      {image && (
        <div className="absolute inset-0 -z-10">
          <BgImage src={image} className="opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
        </div>
      )}
      <SignalField />
      <div className="mx-auto max-w-[1280px] px-6 pb-24 pt-40 lg:px-10 lg:pb-28 lg:pt-48">
        <Reveal>
          <EyebrowLabel light>{eyebrow}</EyebrowLabel>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="mt-6 max-w-4xl text-[clamp(2.6rem,5vw,4.2rem)] font-black leading-[1.02] tracking-[-0.035em]">
            {title}
          </h1>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-7 max-w-2xl text-[16px] leading-[1.7] text-neutral-300">
            {intro}
          </p>
        </Reveal>
      </div>
    </section>
  )
}

/* ----------------------------------------------------------------------------
   Content cards: one design for a case study and one for an article, used by
   the homepage, the listing pages and the "more" rails on detail pages.
---------------------------------------------------------------------------- */
type CaseSummary = {
  slug: string
  client: string
  industry: string
  year: string
  cover: string
  summary: string
  headlineResult: string
}

function CaseMeta({ w, light = false }: { w: CaseSummary; light?: boolean }) {
  const parts = [w.industry, w.year].filter(Boolean)
  if (!parts.length) return null
  return (
    <p
      className={`line-clamp-1 text-[12px] font-semibold uppercase tracking-[0.16em] ${
        light ? "text-neutral-400" : "text-neutral-500"
      }`}
    >
      {parts.join(" · ")}
    </p>
  )
}

function CaseResult({ result, large = false }: { result: string; large?: boolean }) {
  return (
    <p
      className={`inline-flex items-center gap-2 font-semibold text-accent ${
        large ? "text-[20px]" : "text-[15px]"
      }`}
    >
      <Icon name="rising" className={large ? "h-5 w-5" : "h-4 w-4"} />
      {result}
    </p>
  )
}

/**
 * Case study card. One clear hierarchy: the image, a quiet line of context,
 * the client, one sentence on what we did, and the result as the only thing
 * in colour. Nothing is layered on top of the image.
 */
export function CaseCard({ work: w }: { work: CaseSummary }) {
  return (
    <Link
      to={`/work/${w.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white transition-all duration-500 hover:-translate-y-1 hover:border-neutral-300 hover:shadow-[0_24px_50px_-30px_rgba(11,16,23,0.28)]"
    >
      <Img
        src={w.cover}
        monogram={initials(w.client)}
        alt={w.industry ? `${w.client}, ${w.industry}` : w.client}
        sizes="(min-width: 1280px) 600px, (min-width: 640px) 50vw, 100vw"
        className="aspect-[16/10] [&_img]:group-hover:scale-[1.04]"
      />
      <div className="flex flex-1 flex-col p-7">
        {/* The text grows, which is what keeps the footer line level across a
            row of cards whose summaries run to different lengths. */}
        <div className="flex-1">
          <CaseMeta w={w} />
          <h3 className="mt-2.5 text-[21px] font-bold leading-snug tracking-[-0.02em]">
            {w.client}
          </h3>
          {w.summary && (
            <p className="mt-2.5 line-clamp-2 text-[15px] leading-[1.65] text-neutral-600">
              {w.summary}
            </p>
          )}
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-neutral-100 pt-5">
          {w.headlineResult ? (
            <CaseResult result={w.headlineResult} />
          ) : (
            <span />
          )}
          {/* A span, not a button: the whole card is already the link, and
              nesting an interactive element inside one is invalid. */}
          <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-neutral-300 px-4 py-2 text-[13px] font-semibold text-ink transition-colors duration-300 group-hover:border-ink group-hover:bg-ink group-hover:text-white">
            View case study
            <Arrow className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  )
}

/** The lead project on the Work page: image and story side by side. */
export function FeaturedCase({ work: w }: { work: CaseSummary }) {
  return (
    <Link
      to={`/work/${w.slug}`}
      className="group grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14"
    >
      <Img
        src={w.cover}
        monogram={initials(w.client)}
        alt={w.industry ? `${w.client}, ${w.industry}` : w.client}
        sizes="(min-width: 1280px) 700px, (min-width: 1024px) 58vw, 100vw"
        className="aspect-[4/3] rounded-2xl lg:col-span-7 [&_img]:group-hover:scale-[1.03]"
      />
      <div className="lg:col-span-5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">
          Featured project
        </span>
        <div className="mt-5">
          <CaseMeta w={w} />
        </div>
        <h2 className="mt-2 text-[clamp(1.9rem,3.2vw,2.6rem)] font-black leading-[1.06] tracking-[-0.03em] decoration-[3px] underline-offset-[6px] group-hover:underline">
          {w.client}
        </h2>
        {w.summary && (
          <p className="mt-4 text-[16px] leading-[1.7] text-neutral-600">
            {w.summary}
          </p>
        )}
        {w.headlineResult && (
          <div className="mt-6 border-t border-neutral-200 pt-6">
            <CaseResult result={w.headlineResult} large />
          </div>
        )}
        <span className="mt-8 inline-flex items-center gap-2 text-[14px] font-semibold text-ink">
          Read the case study
          <Arrow className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  )
}

export function PostCard({
  post: a,
}: {
  post: {
    slug: string
    title: string
    cover: string
    category: string
    readTime: string
    excerpt: string
  }
}) {
  return (
    <Link
      to={`/insights/${a.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-30px_rgba(11,16,23,0.3)]"
    >
      <Img
        src={a.cover}
        label={a.category}
        alt={a.title}
        sizes="(min-width: 1280px) 400px, (min-width: 1024px) 33vw, 100vw"
        className="aspect-[16/10] [&_img]:group-hover:scale-[1.04]"
      />
      <div className="flex flex-1 flex-col p-7">
        <div className="flex items-center gap-3 text-[11.5px] font-semibold uppercase tracking-[0.14em]">
          <span className="text-accent">{a.category}</span>
          {a.readTime && (
            <>
              <span className="text-neutral-300">·</span>
              <span className="text-neutral-500">{a.readTime}</span>
            </>
          )}
        </div>
        <h3 className="mt-4 text-[20px] font-bold leading-snug tracking-[-0.02em]">
          {a.title}
        </h3>
        <p className="mt-3 flex-1 text-[14.5px] leading-relaxed text-neutral-600">
          {a.excerpt}
        </p>
        <span className="mt-6 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-ink">
          Read article
          <Arrow className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  )
}

/* ----------------------------------------------------------------------------
   Prose — renders lightweight markdown-ish body content
   Supports:  "## Heading",  "- list item",  and blank-line paragraphs
---------------------------------------------------------------------------- */
export function Prose({ text }: { text: string }) {
  /* A heading is its own block even when its paragraph starts on the next line. */
  const blocks = text
    .replace(/^(## .+)\n(?=\S)/gm, "$1\n\n")
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean)

  return (
    <div className="space-y-6">
      {blocks.map((block, i) => {
        if (block.startsWith("## ")) {
          return (
            <h2
              key={i}
              className="border-t border-neutral-200 pt-10 text-[clamp(1.5rem,2.4vw,2rem)] font-black leading-[1.15] tracking-[-0.02em] first:border-t-0 first:pt-0 [&:not(:first-child)]:mt-12"
            >
              {block.replace(/^##\s+/, "")}
            </h2>
          )
        }
        const lines = block.split("\n")
        if (lines.every((l) => l.trim().startsWith("- "))) {
          return (
            <ul key={i} className="space-y-3">
              {lines.map((l, j) => (
                <li
                  key={j}
                  className="flex items-start gap-3 text-[17px] leading-[1.7] text-neutral-700"
                >
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {l.replace(/^-\s+/, "")}
                </li>
              ))}
            </ul>
          )
        }
        return (
          <p key={i} className="text-[17px] leading-[1.8] text-neutral-700">
            {block}
          </p>
        )
      })}
    </div>
  )
}

/* ----------------------------------------------------------------------------
   Logo
---------------------------------------------------------------------------- */
/*
  The light variant is a real asset, not a filtered copy of the dark one. The
  mark contains a cyan cube, and `invert()` turned it orange — the one part of
  the logo that must not shift hue.

  Both variants render and cross-fade rather than swapping `src`, because the
  header toggles `light` on scroll: a swap fetches the other file mid-scroll
  and flashes on the first toggle. `priority` marks the header instance, which
  is above the fold; the footer instance stays lazy.
*/
export function Logo({
  light = false,
  priority = false,
}: {
  light?: boolean
  priority?: boolean
}) {
  const loading = {
    width: 1200,
    height: 203,
    loading: priority ? ("eager" as const) : ("lazy" as const),
    fetchPriority: priority ? ("high" as const) : ("auto" as const),
    decoding: priority ? ("sync" as const) : ("async" as const),
  }

  return (
    <Link to="/" aria-label="Sableboxx home" className="inline-flex items-center">
      <span className="relative inline-block h-7">
        <img
          {...loading}
          src={logoUrl}
          alt="Sableboxx"
          className={`h-7 w-auto transition-opacity duration-500 ${
            light ? "opacity-0" : "opacity-100"
          }`}
        />
        <img
          {...loading}
          src={logoLightUrl}
          alt=""
          aria-hidden="true"
          className={`absolute left-0 top-0 h-7 w-auto transition-opacity duration-500 ${
            light ? "opacity-100" : "opacity-0"
          }`}
        />
      </span>
    </Link>
  )
}

/* ----------------------------------------------------------------------------
   Header
---------------------------------------------------------------------------- */
const NAV = [
  { label: "About Us", to: "/about" },
  { label: "Our Services", to: "/#services" },
  { label: "Case Studies", to: "/work" },
  { label: "Government", to: "/government" },
  // { label: "Process", to: "/process" },
  { label: "Insights", to: "/insights" },
]

/*
  NavLink matches on pathname only, so "/#services" counted as active on the
  whole homepage and the Services link stayed underlined everywhere on it. A
  hash link is active only when the hash matches too; a plain link when the
  pathname starts with it.
*/
function isNavActive(to: string, pathname: string, hash: string) {
  const [path, anchor] = to.split("#")
  if (anchor) return pathname === path && hash === `#${anchor}`
  return pathname === to || pathname.startsWith(`${to}/`)
}

/*
  Insights is listed only once something is published there: a navigation link
  to an empty page undersells the site. The prerendered snapshot carries the
  posts, so server and client agree on whether it shows.
*/
function useHasPosts() {
  return useCollection("posts").items.some((p) => p.status === "Published")
}

function useNav() {
  const hasPosts = useHasPosts()
  return NAV.filter((n) => n.to !== "/insights" || hasPosts)
}

export function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [menu, setMenu] = useState(false)
  const { pathname, hash } = useLocation()
  /* Every public page opens on a dark section, so the header starts
     transparent and turns solid once the page scrolls under it, or when the
     mobile menu opens beneath it. */
  const onDark = !scrolled && !menu
  const nav = useNav()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => setMenu(false), [pathname])

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        menu
          ? "bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]"
          : !onDark
          ? "bg-white/85 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6 py-4 lg:px-10">
        <Logo light={onDark} priority />
        <nav className="hidden items-center gap-9 md:flex">
          {nav.map((n) => {
            const isActive = isNavActive(n.to, pathname, hash)
            return (
              <Link
                key={n.to}
                to={n.to}
                aria-current={isActive ? "page" : undefined}
                className={`group relative text-[13px] font-medium transition-colors ${
                  onDark
                    ? "text-white/75 hover:text-white"
                    : isActive
                      ? "text-ink"
                      : "text-neutral-500 hover:text-ink"
                }`}
              >
                {n.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-px transition-all duration-300 ${
                    onDark ? "bg-accent-bright" : "bg-accent"
                  } ${isActive ? "w-full" : "w-0 group-hover:w-full"}`}
                />
              </Link>
            )
          })}
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            {onDark ? (
              <ButtonLight to="/contact">Contact Us</ButtonLight>
            ) : (
              <ButtonDark to="/contact">Contact Us</ButtonDark>
            )}
          </div>
          <button
            onClick={() => setMenu((m) => !m)}
            aria-label={menu ? "Close menu" : "Open menu"}
            aria-expanded={menu}
            className={`grid h-10 w-10 place-items-center rounded-full border md:hidden ${
              onDark
                ? "border-white/20 text-white"
                : "border-neutral-200 text-ink"
            }`}
          >
            <Icon name={menu ? "close" : "menu"} className="h-5 w-5" />
          </button>
        </div>
      </div>

      {menu && (
        <div className="border-t border-neutral-200 bg-white md:hidden">
          <div className="mx-auto max-w-[1280px] px-6 py-6">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="block border-b border-neutral-100 py-4 text-[16px] font-medium text-ink"
              >
                {n.label}
              </Link>
            ))}
            <div className="pt-6">
              <ButtonDark to="/contact">Contact Us</ButtonDark>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

/* ----------------------------------------------------------------------------
   Footer
---------------------------------------------------------------------------- */
const FOOTER_COLS = [
  {
    h: "Explore",
    items: [
      { l: "About Us", to: "/about" },
      { l: "Case Studies", to: "/work" },
      { l: "Government", to: "/government" },
      { l: "Our Services", to: "/#services" },
      // { l: "Process", to: "/process" },
      { l: "Insights", to: "/insights" },
      // { l: "Project Questionnaire", to: "/questionnaire" },
    ],
  },
  {
    h: "Services",
    items: [
      { l: "Custom Software", to: "/#services" },
      { l: "Digital Transformation", to: "/#services" },
      { l: "Government & Institutional", to: "/government" },
      { l: "Maintenance & Support", to: "/#services" },
    ],
  },
]

/* Social profiles and email leave the site, so they are anchors rather than
   router <Link>s and get the usual rel hardening. */
const FOOTER_CONNECT = [
  ...SITE.socialLinks,
  { label: "Email", href: `mailto:${SITE.email}` },
]

export function Footer() {
  const hasPosts = useHasPosts()
  const footerCols = FOOTER_COLS.map((c) => ({
    ...c,
    items: c.items.filter((it) => it.to !== "/insights" || hasPosts),
  }))
  return (
    <footer className="border-t border-white/10 bg-ink text-white">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="grid grid-cols-2 gap-x-6 gap-y-12 py-20 lg:grid-cols-12">
          <div className="col-span-2 lg:col-span-4">
            <Logo light />
            <p className="mt-5 text-[12px] font-semibold uppercase tracking-[0.2em] text-accent-bright">
              Software · Digital Infrastructure
            </p>
            <p className="mt-4 max-w-xs text-[14px] leading-relaxed text-neutral-400">
              A software engineering company in Abuja, building intelligent
              systems that help businesses and institutions operate better.
            </p>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-[12px] font-medium uppercase tracking-[0.18em] text-neutral-400">
              Connect
            </h2>
            <ul className="mt-4 space-y-1">
              {FOOTER_CONNECT.map((it) => (
                <li key={it.label}>
                  <a
                    href={it.href}
                    target={it.href.startsWith("mailto:") ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    className="inline-block py-1 text-[14px] text-neutral-300 transition-colors hover:text-white"
                  >
                    {it.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {footerCols.map((c) => (
            <div key={c.h} className="lg:col-span-2">
              <h2 className="text-[12px] font-medium uppercase tracking-[0.18em] text-neutral-400">
                {c.h}
              </h2>
              <ul className="mt-4 space-y-1">
                {c.items.map((it) => (
                  <li key={it.l}>
                    <Link
                      to={it.to}
                      className="inline-block py-1 text-[14px] text-neutral-300 transition-colors hover:text-white"
                    >
                      {it.l}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h2 className="text-[12px] font-medium uppercase tracking-[0.18em] text-neutral-400">
              Location
            </h2>
            <p className="mt-5 text-[14px] leading-relaxed text-neutral-300">
              {SITE.address.city}, {SITE.address.countryName}
              <br />
              Working with clients anywhere
            </p>
            <a
              href={`tel:${SITE.phoneHref}`}
              className="mt-3 inline-block py-1 text-[14px] text-neutral-300 transition-colors hover:text-white"
            >
              {SITE.phone}
            </a>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-white/10 py-8 text-[12.5px] text-neutral-400 md:flex-row md:items-center md:justify-between">
          <p>&copy; {BUILD_YEAR} Sableboxx. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="py-1 transition-colors hover:text-white">
              Privacy
            </Link>
            <Link to="/terms" className="py-1 transition-colors hover:text-white">
              Terms
            </Link>
            {/* <Link
              to="/admin"
              className="transition-colors hover:text-white"
            >
              Studio login
            </Link> */}
          </div>
        </div>
      </div>
    </footer>
  )
}

/* ----------------------------------------------------------------------------
   Shared closing CTA
---------------------------------------------------------------------------- */
export function FinalCta() {
  return (
    <section className="relative isolate overflow-hidden bg-ink text-white">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_80%_at_50%_120%,rgba(23,76,138,0.55)_0%,transparent_70%)]" />
      <SignalField />
      <div className="mx-auto max-w-[1280px] px-6 py-28 lg:px-10 lg:py-36 lg:text-center">
        <Reveal>
          <div className="flex lg:justify-center">
            <EyebrowLabel light>Start a conversation</EyebrowLabel>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <h2 className="mt-7 max-w-3xl text-[clamp(2.4rem,5vw,4rem)] font-black leading-[1.04] tracking-[-0.035em] lg:mx-auto">
            Have a system to build or fix? Let’s talk.
          </h2>
        </Reveal>
        <Reveal delay={180}>
          <p className="mt-7 max-w-xl text-[16px] leading-[1.7] text-neutral-400 lg:mx-auto">
            Tell us about the operation, the process that isn’t working, or 
            the procurement you’re preparing. We’ll come back with clear 
            next steps.
          </p>
        </Reveal>
        <Reveal delay={260}>
          <div className="mt-11 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-8 lg:justify-center">
            <ButtonLight to="/contact">Contact Us</ButtonLight>
            {/* <a
              href={`mailto:${SITE.email}`}
              className="text-[14px] font-medium text-neutral-300 underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              or email {SITE.email}
            </a> */}
          </div>
        </Reveal>
      </div>
    </section>
  )
}

/* ----------------------------------------------------------------------------
   LegalPage — shared shell for Privacy Policy and Terms of Service.

   Deliberately plain: legal copy is read, not scanned, so it gets a single
   measured column, generous leading and real heading hierarchy rather than the
   marketing pages' full-bleed imagery.
---------------------------------------------------------------------------- */
export function LegalPage({
  title,
  updated,
  intro,
  children,
}: {
  title: string
  /** ISO date the document last changed. Rendered and machine-readable. */
  updated: string
  intro: string
  children: React.ReactNode
}) {
  return (
    <>
      <section className="border-b border-neutral-200 bg-ink text-white">
        <div className="mx-auto max-w-[1280px] px-6 pb-20 pt-40 lg:px-10 lg:pb-24 lg:pt-48">
          <Reveal>
            <EyebrowLabel light>Legal</EyebrowLabel>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 max-w-3xl text-[clamp(2.4rem,4.6vw,3.6rem)] font-black leading-[1.04] tracking-[-0.035em]">
              {title}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-7 max-w-2xl text-[16px] leading-[1.7] text-neutral-300">
              {intro}
            </p>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-8 text-[12px] font-medium uppercase tracking-[0.16em] text-neutral-400">
              Last updated{" "}
              <time dateTime={updated}>
                {new Date(updated).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </time>
            </p>
          </Reveal>
        </div>
      </section>

      <Section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-[760px]">{children}</div>
      </Section>
    </>
  )
}

/** A numbered top-level clause inside <LegalPage>. */
export function LegalSection({
  n,
  heading,
  children,
}: {
  n: number
  heading: string
  children: React.ReactNode
}) {
  return (
    <section className="border-t border-neutral-200 py-10 first:border-t-0 first:pt-0">
      <h2 className="flex gap-4 text-[22px] font-bold leading-snug tracking-[-0.02em]">
        <span className="shrink-0 text-neutral-300 tabular-nums">
          {String(n).padStart(2, "0")}
        </span>
        {heading}
      </h2>
      <div className="mt-5 space-y-4 pl-0 text-[15.5px] leading-[1.75] text-neutral-600 sm:pl-[3.25rem] [&_a]:font-medium [&_a]:text-ink [&_a]:underline [&_a]:underline-offset-4 [&_li]:relative [&_li]:pl-5 [&_strong]:font-semibold [&_strong]:text-ink [&_ul]:space-y-2.5">
        {children}
      </div>
    </section>
  )
}

/** Bulleted list with the studio's marker, for use inside <LegalSection>. */
export function LegalList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul>
      {items.map((it, i) => (
        <li key={i}>
          <span className="absolute left-0 top-[0.7em] h-1.5 w-1.5 rounded-full bg-accent" />
          {it}
        </li>
      ))}
    </ul>
  )
}
